import React, { CSSProperties, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

interface MarkdownDescriptionProps {
  label: string;
  value: string;
  /** Event-based change handler (matches the existing textarea handlers). */
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Height of the rendered/edit area in px. */
  minHeight?: number;
  emptyHint?: string;
}

// Shared description field with a Markdown ↔ Raw toggle. Markdown view is the
// read-only default (click anywhere to switch to Raw and edit); Raw is a plain
// textarea. Used by both Task Detail and Note Detail so the behaviour is
// identical in both places.
const MarkdownDescription: React.FC<MarkdownDescriptionProps> = ({
  label,
  value,
  onChange,
  minHeight = 160,
  emptyHint = "_No description yet. Click to edit._",
}) => {
  const [mode, setMode] = useState<"markdown" | "raw">("markdown");

  return (
    <div style={{ marginTop: "12px" }}>
      <div style={styles.headerRow}>
        <label>{label}</label>
        <div style={styles.row}>
          <button
            type="button"
            onClick={() => setMode("markdown")}
            style={mode === "markdown" ? styles.on : styles.off}
          >
            Markdown
          </button>
          <button
            type="button"
            onClick={() => setMode("raw")}
            style={mode === "raw" ? styles.on : styles.off}
          >
            Raw
          </button>
        </div>
      </div>

      {mode === "markdown" ? (
        <div
          className="note-markdown"
          onClick={() => setMode("raw")}
          title="Click to edit"
          style={{ ...styles.markdownBox, minHeight: `${minHeight}px` }}
        >
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {value || emptyHint}
          </ReactMarkdown>
        </div>
      ) : (
        <textarea
          value={value}
          onChange={onChange}
          autoFocus
          style={{ ...styles.textarea, height: `${minHeight}px` }}
        />
      )}
    </div>
  );
};

const pillBase: CSSProperties = {
  padding: "6px 14px",
  borderRadius: "16px",
  cursor: "pointer",
  fontSize: "13px",
};

const styles: Record<string, CSSProperties> = {
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  row: { display: "flex", gap: "8px" },
  off: { ...pillBase, border: "1px solid var(--border)", background: "transparent", color: "var(--text)" },
  on: { ...pillBase, border: "1px solid var(--primary)", background: "var(--primary)", color: "#fff" },
  markdownBox: {
    width: "100%",
    padding: "12px",
    borderRadius: "4px",
    border: "1px solid var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--text)",
    boxSizing: "border-box",
    overflowY: "auto",
    cursor: "text",
  },
  textarea: {
    width: "100%",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--text)",
    boxSizing: "border-box",
  },
};

export default MarkdownDescription;
